import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal } from 'react-native';
import { format, addDays, subDays } from 'date-fns';
import { Calendar } from 'react-native-calendars';
import { ReportType } from '../types';
import colors from '@constants/color';
import { ScaledSheet } from '@utils/scale';

interface DatePickerProps {
  dateText: string;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  reportType: ReportType;
}

/**
 * DatePicker Component
 * Allows navigation between dates/weeks and calendar selection
 */
const DatePicker: React.FC<DatePickerProps> = ({
  dateText,
  selectedDate,
  onDateChange,
  reportType,
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
console.log('selectedDate', dateText)
  const handlePreviousDate = () => {
    const daysToSubtract = reportType === 'WEEKLY' ? 7 : 1;
    const newDate = subDays(selectedDate, daysToSubtract);
    onDateChange(newDate);
  };

  const handleNextDate = () => {
    const daysToAdd = reportType === 'WEEKLY' ? 7 : 1;
    const newDate = addDays(selectedDate, daysToAdd);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    // Prevent navigating to future dates
    if (newDate <= today) {
      onDateChange(newDate);
    }
  };

  const handleDateSelect = (day: any) => {
    const selectedDateObj = new Date(day.dateString);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    
    if (selectedDateObj <= today) {
      onDateChange(selectedDateObj);
      setShowCalendar(false);
    }
  };

  const formatDate = () => {
    return format(selectedDate, 'd MMM yyyy');
  };

  const getFormattedDateString = () => {
    return format(selectedDate, 'yyyy-MM-dd');
  };

  return (
    <>
      <View style={styles.container}>
        <TouchableOpacity
          style={styles.arrowButton}
          onPress={handlePreviousDate}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Text style={styles.arrowText}>‹</Text>
        </TouchableOpacity>

        <TouchableOpacity 
          style={styles.dateContainer}
          onPress={() => setShowCalendar(true)}
        >
          <Text style={styles.dateText}>{dateText}</Text>
          <Text style={styles.dropdownIcon}>▼</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.arrowButton}
          onPress={handleNextDate}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Text style={styles.arrowText}>›</Text>
        </TouchableOpacity>
      </View>

      {/* Calendar Modal */}
      <Modal
        statusBarTranslucent
        navigationBarTranslucent
        visible={showCalendar}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCalendar(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCalendar(false)}
        >
          <View style={styles.calendarContainer}>
            <Calendar
              onDayPress={handleDateSelect}
              minDate={(() => {
                const date = new Date();
                date.setMonth(date.getMonth() - 2);
                return date.toISOString().split('T')[0];
              })()}
              maxDate={new Date().toISOString().split('T')[0]}
              markedDates={{
                [getFormattedDateString()]: { selected: true, selectedColor: colors.primary },
              }}
              theme={{
                backgroundColor: colors.white,
                calendarBackground: colors.white,
                textSectionTitleColor: '#000000',
                selectedDayBackgroundColor: colors.primary,
                selectedDayTextColor: colors.white,
                todayTextColor: colors.primary,
                dayTextColor: '#000000',
                textDisabledColor: '#D3D3D3',
                monthTextColor: '#000000',
                textMonthFontWeight: '600',
                textDayFontSize: 16,
                textMonthFontSize: 18,
              }}
            />
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const styles = ScaledSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  arrowButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  arrowText: {
    fontSize: 28,
    color: '#000000',
    fontWeight: '300',
  },
  dateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EAEBEC',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 10,
    marginHorizontal: 8,
    gap: 8,
  },
  dateText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#000000',
  },
  dropdownIcon: {
    fontSize: 10,
    color: '#666666',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  calendarContainer: {
    backgroundColor: colors.white,
    borderRadius: 16,
    padding: 16,
    width: '90%',
    maxWidth: 400,
  },
});

export default DatePicker;


